import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_URL}${url}`;
  return url;
}

export default function ImageUploader({ value, onChange, label = 'Kiemelt kép' }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_SIZE = 5 * 1024 * 1024;

  const uploadFile = useCallback(async (file) => {
    setError('');
    const accepted = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!accepted.includes(file.type)) {
      setError('Csak JPG, PNG vagy WEBP formátum engedélyezett.');
      return;
    }
    if (file.size > maxSize) {
      setError('A fájl mérete nem haladhatja meg az 5 MB-ot.');
      return;
    }

    setUploading(true);
    setProgress(10);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress steps
      const progressTimer = setInterval(() => {
        setProgress(p => Math.min(p + 15, 85));
      }, 200);

      const { data } = await api.post('/upload/image', formData);

      clearInterval(progressTimer);
      setProgress(100);
      onChange(data.url);

      setTimeout(() => setProgress(0), 500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Képfeltöltés sikertelen. Kérjük próbálja újra.';
      setError(msg);
      console.error('Upload error:', err.response?.status, err.response?.data);
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleRemove = () => {
    onChange('');
    setError('');
  };

  const imageUrl = resolveImageUrl(value);

  return (
    <div data-testid="image-uploader">
      <label className="text-sm font-medium text-[#1A1F2C] block mb-1.5">{label}</label>

      {imageUrl ? (
        /* Preview mode */
        <div className="relative group">
          <div className="relative rounded-lg overflow-hidden border border-[#E2E8F0] bg-[#FAFAFA]">
            <img
              src={imageUrl}
              alt="Feltöltött kép"
              className="w-full max-h-64 object-contain"
              data-testid="image-preview"
            />
            {/* Overlay actions */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
              <label className="px-4 py-2 bg-white rounded-lg text-sm font-medium text-[#0B1B3D] cursor-pointer hover:bg-[#F1F5F9] transition-colors shadow-sm">
                Csere
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                onClick={handleRemove}
                data-testid="image-remove-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors shadow-sm"
              >
                Törlés
              </button>
            </div>
          </div>
          <p className="text-xs text-[#64748B] mt-1.5 truncate">{value}</p>
        </div>
      ) : (
        /* Upload zone */
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`relative rounded-lg border-2 border-dashed transition-all duration-200 ${
            dragOver
              ? 'border-[#C5A059] bg-[#C5A059]/5'
              : 'border-[#E2E8F0] bg-[#FAFAFA] hover:border-[#94A3B8]'
          }`}
        >
          <label className="flex flex-col items-center justify-center py-8 px-4 cursor-pointer">
            {uploading ? (
              <Loader2 size={32} className="text-[#C5A059] animate-spin mb-3" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#0B1B3D]/5 flex items-center justify-center mb-3">
                <ImageIcon size={22} className="text-[#64748B]" />
              </div>
            )}
            <span className="text-sm font-medium text-[#1A1F2C]">
              {uploading ? 'Feltöltés folyamatban...' : 'Kép feltöltése'}
            </span>
            <span className="text-xs text-[#64748B] mt-1">
              {uploading ? '' : 'Húzza ide a fájlt, vagy kattintson a tallózáshoz'}
            </span>
            <span className="text-xs text-[#94A3B8] mt-1">JPG, PNG, WEBP (max. 5 MB)</span>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="hidden"
              disabled={uploading}
            />
          </label>

          {/* Progress bar */}
          {uploading && progress > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#E2E8F0] rounded-b-lg overflow-hidden">
              <div
                className="h-full bg-[#C5A059] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 mt-1.5" data-testid="image-upload-error">{error}</p>
      )}
    </div>
  );
}

export { resolveImageUrl };
